import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

interface PlaceholderProps {
  /** Optional label (e.g. "Coming soon") */
  label?: string;
  /** Aspect ratio as number, e.g. 16/9 or 1 for square */
  aspectRatio?: number;
  /** Aspect ratio for mobile only (e.g. 3/4 for vertical/portrait). Used in sx for xs breakpoint. */
  aspectRatioMobile?: number;
  /** Inline width (e.g. "100%", "320px"). Default fills container. */
  width?: string | number;
  /** Inline height. If aspectRatio is set, height can be omitted. */
  height?: string | number;
  /** Optional subtitle */
  subtitle?: string;
}

export default function Placeholder({
  label = "Coming soon",
  aspectRatio = 4 / 3,
  aspectRatioMobile,
  width = "100%",
  height,
  subtitle,
}: PlaceholderProps) {
  let computedAspectRatio;

  if (aspectRatioMobile) {
    computedAspectRatio = {
      xs: `${aspectRatioMobile}`,
      md: `${aspectRatio}`,
    };
  } else if (aspectRatio) {
    computedAspectRatio = `${aspectRatio}`;
  } else {
    computedAspectRatio = undefined;
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0.6 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{
        width,
        height: height ?? "auto",
        aspectRatio: computedAspectRatio,
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          linear-gradient(135deg, 
            rgba(249, 131, 55, 0.08) 0%, 
            rgba(100, 124, 209, 0.06) 50%,
            rgba(249, 131, 55, 0.04) 100%
          )
        `,
        border: "1px dashed rgba(0,0,0,0.12)",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(45deg, transparent 48%, rgba(0,0,0,0.03) 50%, transparent 52%),
            linear-gradient(-45deg, transparent 48%, rgba(0,0,0,0.03) 50%, transparent 52%)
          `,
          backgroundSize: "24px 24px",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          px: 3,
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: 10,
            height: 10,
            border: "1.5px solid rgba(0,0,0,0.35)",
            transform: "rotate(45deg)",
            mb: 1.5,
          }}
        />
        <Typography
          variant="body2"
          fontFamily="Aktiv"
          sx={{
            fontSize: 13,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(0,0,0,0.5)",
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            fontFamily="Aktiv"
            sx={{
              fontSize: 12,
              color: "rgba(0,0,0,0.35)",
              mt: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
